import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import AsyncSelect from "react-select/async";
import BaseTemplate from "../components/BaseTemplate";
import PersistentGrid from "../components/PersistentGrid/PersistentGrid";
import PersistentGridResetButton from "../components/PersistentGrid/PersistentGridResetButton";
import PersistentGridClearFiltersButton from "../components/PersistentGrid/PersistentGridClearFiltersButton";
import { PersistentGridProvider } from "../contexts/PersistentGridContext";
import { useApi } from "../hooks/useApi";
import { NotificationContext } from "../components/NotificationPane";
import type { NotificationContextType, ReferenceMapping, ReferenceMappingInput } from "../types";
import InternalUserRoute from "../util/internal_user_route";
import { buildDefaultColDef, EDB_CUSTOM_withHeaderTooltips } from "../util/AgGridFunctions";
import { useColumnDefaults } from "../hooks/useColumnDefaults";

type EditableReferenceRow = {
  account_alias: string;
  crm_account: string;
  account_id: string;
};

type CrmOption = { label: string; value: string };

type AccountMappingFieldsProps = {
  idSuffix: string;
  form: EditableReferenceRow;
  onFieldChange: (
    field: keyof EditableReferenceRow,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedCrm: CrmOption | null;
  onCrmChange: (option: CrmOption | null) => void;
  loadCrmAccountOptions: (input: string) => Promise<CrmOption[]>;
};

function AccountMappingFields({
  idSuffix,
  form,
  onFieldChange,
  selectedCrm,
  onCrmChange,
  loadCrmAccountOptions,
}: AccountMappingFieldsProps) {
  return (
    <VStack spacing={3} align="stretch">
      <FormControl>
        <FormLabel>Account Alias</FormLabel>
        <Input
          value={form.account_alias}
          onChange={onFieldChange("account_alias")}
          maxLength={100}
        />
      </FormControl>

      <FormControl>
        <FormLabel>CRM Account</FormLabel>
        <AsyncSelect
          inputId={`crmAccount${idSuffix}`}
          placeholder="Start typing..."
          cacheOptions
          defaultOptions
          loadOptions={loadCrmAccountOptions}
          getOptionValue={(o) => o.value}
          getOptionLabel={(o) => o.label}
          value={selectedCrm}
          onChange={(opt) => onCrmChange(opt as CrmOption | null)}
          isClearable={false}
          styles={{ menu: (p) => ({ ...p, zIndex: 10 }) }}
        />
      </FormControl>
    </VStack>
  );
}

function AccountMapping() {
  const {
    getReferenceMappings,
    getSuggestedAccounts,
    updateReferenceMapping,
    addReferenceMapping,
  } = useApi();
  const columnDefaults = useColumnDefaults();
  const { pushNotification } =
    useContext<NotificationContextType | null>(NotificationContext)!;
  const [rowData, setRowData] = useState<ReferenceMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const [selectedRow, setSelectedRow] = useState<ReferenceMapping | null>(null);
  const [selectedCrm, setSelectedCrm] = useState<CrmOption | null>(null);
  const [selectedAddCrm, setSelectedAddCrm] = useState<CrmOption | null>(null);
  const [editForm, setEditForm] = useState<EditableReferenceRow>({
    account_alias: "",
    crm_account: "",
    account_id: "",
  });
  const [addForm, setAddForm] = useState<EditableReferenceRow>({
    account_alias: "",
    crm_account: "",
    account_id: "",
  });

  const loadReferenceMappings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getReferenceMappings();
      setRowData(data);
      setError(null);
    } catch (err) {
      console.error("Error loading reference mappings", err);
      setError("Failed to load reference mappings.");
      pushNotification("Failed to load reference mappings.", "error");
    } finally {
      setLoading(false);
    }
  }, [getReferenceMappings, pushNotification]);

  useEffect(() => {
    loadReferenceMappings();
  }, [loadReferenceMappings]);

  const columnDefs = useMemo<ColDef<ReferenceMapping>[]>(
    () => EDB_CUSTOM_withHeaderTooltips([
      { field: "sk", headerName: "SK", ...buildDefaultColDef(columnDefaults, "sk"), minWidth: 100 },
      { field: "account_alias", headerName: "Account Alias", ...buildDefaultColDef(columnDefaults, "account_alias"), minWidth: 180 },
      { field: "crm_account", headerName: "CRM Account", ...buildDefaultColDef(columnDefaults, "crm_account"), minWidth: 180 },
      // Read-only: crm_copy.account.eb_account_code via account_id, '' when none (ref.crm_account_mapping_wv).
      { field: "crm_account_code", headerName: "CRM Account Code", ...buildDefaultColDef(columnDefaults, "crm_account_code"), minWidth: 160 },
      { field: "account_id", headerName: "CRM Account ID", ...buildDefaultColDef(columnDefaults, "account_id"), minWidth: 180 },
      { field: "isPicklist", headerName: "Is Picklist", ...buildDefaultColDef(columnDefaults, "isPicklist"), minWidth: 140 },
    ]),
    [columnDefaults],
  );

  const defaultColDef = useMemo<ColDef<ReferenceMapping>>(
    () => ({
      ...buildDefaultColDef(columnDefaults),
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      minWidth: 120,
      flex: 1,
    }),
    [columnDefaults],
  );

  const loadCrmAccountOptions = async (input: string): Promise<CrmOption[]> => {
    if (!input || input.trim().length < 2) return [];
    const matches = await getSuggestedAccounts(input.trim());
    return matches.map(({ account_name, account_id }) => ({
      label: account_name,
      value: account_id,
    }));
  };

  const updateReferenceRow = async (updatedRow: ReferenceMapping) => {
    await updateReferenceMapping(updatedRow);
    await loadReferenceMappings();
  };

  const addReferenceRow = (newRow: ReferenceMappingInput) => {
    return addReferenceMapping(newRow);
  };

  const handleRowClicked = (event: RowClickedEvent<ReferenceMapping>) => {
    if (!event.data) return;

    const row = event.data;
    setSelectedRow(row);
    setEditForm({
      account_alias: row.account_alias ?? "",
      crm_account: row.crm_account ?? "",
      account_id: row.account_id ?? "",
    });
    setSelectedCrm(
      row.account_id
        ? {
            label: row.crm_account ?? "",
            value: row.account_id,
          }
        : null,
    );
    onEditOpen();
  };

  const handleInputChange =
    (field: keyof EditableReferenceRow) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleEditCancel = () => {
    setSelectedRow(null);
    setSelectedCrm(null);
    onEditClose();
  };

  const handleAddInputChange =
    (field: keyof EditableReferenceRow) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const openAddModal = () => {
    setSelectedAddCrm(null);
    setAddForm({
      account_alias: "",
      crm_account: "",
      account_id: "",
    });
    onAddOpen();
  };

  const handleAddCancel = () => {
    setSelectedAddCrm(null);
    onAddClose();
  };

  const handleEditSave = async () => {
    if (!selectedRow) return;

    const updatedRow: ReferenceMapping = {
      ...selectedRow,
      ...editForm,
    };

    try {
      await updateReferenceRow(updatedRow);
      pushNotification("Reference row updated successfully.", "success");
      handleEditCancel();
    } catch (err) {
      console.error("Error updating reference row", err);
      setError("Failed to update reference row.");
      pushNotification("Failed to update reference row.", "error");
    }
  };

  const handleAddSave = async () => {
    const newRow: ReferenceMappingInput = {
      account_alias: addForm.account_alias,
      crm_account: addForm.crm_account,
      account_id: addForm.account_id,
    };

    try {
      await addReferenceRow(newRow);
      await loadReferenceMappings();
      pushNotification("Reference row added successfully.", "success");
      handleAddCancel();
    } catch (err) {
      console.error("Error adding reference row", err);
      setError("Failed to add reference row.");
      pushNotification("Failed to add reference row.", "error");
    }
  };

  return (
    <InternalUserRoute>
      <PersistentGridProvider gridKey="reference-crm-account-mapping-grid">
        <BaseTemplate fitToWindow={true}>
          <BaseTemplate.Header>
            <HStack>
              <PersistentGridResetButton />
              <PersistentGridClearFiltersButton />
              <Button onClick={openAddModal}>Add</Button>
              {error && <Text color="red.500">{error}</Text>}
            </HStack>
          </BaseTemplate.Header>
          <BaseTemplate.Body>
            <Box w="100%" h="100%">
              <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                <PersistentGrid<ReferenceMapping>
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  loading={loading}
                  animateRows
                  onRowClicked={handleRowClicked}
                  getRowStyle={() => ({ cursor: "pointer" })}
                />
              </div>
            </Box>
          </BaseTemplate.Body>
        </BaseTemplate>
      </PersistentGridProvider>

      <Modal isOpen={isEditOpen} onClose={handleEditCancel}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Reference Row</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <AccountMappingFields
              idSuffix="Edit"
              form={editForm}
              onFieldChange={handleInputChange}
              selectedCrm={selectedCrm}
              onCrmChange={(option) => {
                setSelectedCrm(option);
                setEditForm((prev) => ({
                  ...prev,
                  crm_account: option?.label ?? "",
                  account_id: option?.value ?? "",
                }));
              }}
              loadCrmAccountOptions={loadCrmAccountOptions}
            />
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleEditCancel}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleEditSave}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isAddOpen} onClose={handleAddCancel}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Reference Row</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <AccountMappingFields
              idSuffix="Add"
              form={addForm}
              onFieldChange={handleAddInputChange}
              selectedCrm={selectedAddCrm}
              onCrmChange={(option) => {
                setSelectedAddCrm(option);
                setAddForm((prev) => ({
                  ...prev,
                  crm_account: option?.label ?? "",
                  account_id: option?.value ?? "",
                }));
              }}
              loadCrmAccountOptions={loadCrmAccountOptions}
            />
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleAddCancel}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleAddSave}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </InternalUserRoute>
  );
}

export default AccountMapping;
